<template>
  <div class="mb-20 mt-3 bg-white-darker py-4 md:mb-0">
    <div class="container md:max-w-[75rem]">
      <h3
        v-t="'pages.extra_options.title'"
        class="sm:md-3 mb-4 text-2xl font-bold"
      />
      <div class="flex flex-col gap-5 lg:flex-row">
        <div
          class="relative h-[380px] w-full fill-gray-400 text-gray-400 sm:block md:gap-5 lg:max-w-xl"
        >
          <div
            class="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-2 text-gray-600"
          >
            <span
              class="absolute inset-0 h-full w-full animate-pulse rounded-md bg-black/10"
            />

            <span
              class="absolute inset-0 flex flex-col items-center justify-center gap-2"
            >
              <SpinnerIcon size="w-8 h-8" />
              <span
                v-t="'pages.extra_options.placeholder_text'"
                class="text-xl"
              />
            </span>
          </div>

          <img
            :alt="$t('pages.extra_options.preview_alt')"
            :src="collectionStore.selectedEndProductValue!.defaultImage.full"
            loading="lazy"
            class="absolute h-full w-full rounded-md object-cover opacity-0 transition-opacity duration-300"
            onload="this.classList.remove('opacity-0')"
          />
        </div>
        <div class="w-full">
          <div class="flex h-full flex-col justify-between">
            <div class="flex flex-col justify-between gap-2">
              <PriceOptionEntry
                :name="collectionStore.selectedEndProductValue!.productName"
                :price="collectionStore.selectedEndProductValue!.localizedPrice"
                :recommended-price="
                  collectionStore.selectedEndProductValue!
                    .localizedRecommendedPrice
                "
              />
              <template
                v-for="(
                  option, categoryId
                ) in extraOptionsStore.selectedOptions"
                :key="categoryId"
              >
                <PriceOptionEntry
                  v-if="option"
                  :name="`${keyedCategories[categoryId]?.name}: ${option.displayName}`"
                  :price="option.localizedPrice"
                  :recommended-price="option.localizedRecommendedPrice"
                />
              </template>
              <hr class="my-2 border-t border-gray-lighter px-2" />
              <div class="flex justify-between font-bold">
                <span v-t="'general.price_total'" />
                <span>
                  <span
                    v-if="
                      collectionStore.totalRecommendedPrice >
                      collectionStore.totalPrice
                    "
                    class="mr-2 text-gray-light line-through"
                    v-text="
                      formatPrice(collectionStore.totalRecommendedPrice / 100)
                    "
                  />
                  <span
                    v-text="formatPrice(collectionStore.totalPrice / 100)"
                  />
                </span>
              </div>

              <div
                v-if="showTotalSavedPrice"
                class="flex w-full justify-end gap-1 text-sm text-green-darker"
              >
                <span v-t="'pages.extra_options.saved_price'" />
                <span
                  class="font-bold"
                  v-text="formatPrice(collectionStore.totalSavedPrice / 100)"
                />
              </div>
            </div>

            <!--     Buttons for medium size screens and up    -->
            <div class="hidden w-full justify-between gap-2 md:flex">
              <BackButton
                always-chevron
                @click="collectionStore.extraOptionsModalOpen = false"
              />

              <AddToCartButton />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SpinnerIcon from '@/components/Icons/SpinnerIcon.vue';
import BackButton from '@/components/Buttons/BackButton.vue';
import { usePriceFormatter } from '@/composables/UsePriceFormatter';
import { useCollectionStore } from '@/stores/Collection/CollectionStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { ProductOptionCategoryObject } from '@/types/joostconnector';
import _ from 'lodash';
import { computed, ComputedRef } from 'vue';
import PriceOptionEntry from '../ExtraOptions/PriceOptionEntry.vue';
import AddToCartButton from './AddToCartButton.vue';

const collectionStore = useCollectionStore();
const extraOptionsStore = useExtraOptionsStore();
const { formatPrice } = usePriceFormatter();

const showTotalSavedPrice: ComputedRef<boolean> = computed(
  () =>
    collectionStore.totalSavedPrice > 0 &&
    collectionStore.totalRecommendedPrice > collectionStore.totalPrice
);

const keyedCategories: ComputedRef<
  Record<number, ProductOptionCategoryObject>
> = computed(() => _.keyBy(extraOptionsStore.productOptionsCategories, 'id'));
</script>
